import { useReducer } from 'react';
import UnreachableCaseError from '../utils/UnreachableCaseError';

export type UseLoadStateReturn = {
  onLoad(): void;
  onError(err: unknown): void;
  isLoading: boolean;
  loaded: boolean;
  error: unknown;
};

export type UseLoadStateParams = {
  onLoad?(): void;
  onError(err: unknown): void;
};

type State = {
  isLoading: boolean;
  loaded: boolean;
  error: unknown;
};

const initialState: State = {
  isLoading: true,
  loaded: false,
  error: null,
};

type Action =
  | {
      type: 'load';
    }
  | {
      type: 'error';
      error: unknown;
    };

function reducer(state: State, action: Action) {
  switch (action.type) {
    case 'load':
      return {
        isLoading: false,
        loaded: true,
        error: null,
      };
    case 'error':
      return {
        isLoading: false,
        loaded: false,
        error: action.error,
      };
    default:
      throw new UnreachableCaseError(action);
  }
}

export function useLoadState({ onError, onLoad }: UseLoadStateParams): UseLoadStateReturn {
  const [{ isLoading, loaded, error }, dispatch] = useReducer(reducer, initialState);
  const load = () => {
    dispatch({ type: 'load' });
    onLoad?.();
  };
  const fail = (error: unknown) => {
    dispatch({ type: 'error', error });
    onError(error);
  };

  return {
    isLoading,
    loaded,
    onLoad: load,
    onError: fail,
    error,
  };
}
